import React, { useEffect, useRef, useState } from 'react';
import type { LoginResponse, SsoProvider, SsoProvidersDto } from '../../../../shared/types';
import { Api } from '../api';
import { runSso, SsoLogo, SSO_LABEL } from '../sso';
import { Icon } from '../icons';
import { Logo } from '../Logo';
import { ErrorBanner, Field, PasswordInput } from '../ui';

// Nella versione web il server e quello che ha servito la pagina.
export const DEFAULT_SERVER = /^https?:$/.test(location.protocol) ? location.origin : 'https://crm.tecnoside.it';

interface Props {
  serverUrl: string;
  error?: string;
  version: string;
  /** quick = salva l'accesso rapido con impronta o viso su questo PC */
  onLogin: (serverUrl: string, email: string, password: string, quick: boolean) => Promise<void>;
  /** Entra con Windows Hello usando l'accesso rapido salvato */
  onQuickLogin: () => Promise<void>;
  /** Accesso riuscito con Google o Microsoft */
  onSsoLogin: (serverUrl: string, r: LoginResponse) => Promise<void>;
}

export function Login({ serverUrl: initialUrl, error: initialError, version, onLogin, onQuickLogin, onSsoLogin }: Props) {
  // Il server non si vede e non si sceglie: chi accede scrive solo email e password. Un vecchio indirizzo locale salvato dalle prove viene ignorato
  const [serverUrl] = useState(/^https?:\/\/(127\.0\.0\.1|localhost)/.test(initialUrl) ? DEFAULT_SERVER : initialUrl);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(initialError ?? null);
  const [busy, setBusy] = useState(false);
  // Impronta o viso: pronto su questo PC? C'è già un accesso rapido salvato?
  const [hello, setHello] = useState(false);
  const [saved, setSaved] = useState<{ email: string; name?: string } | null>(null);
  const [useQuick, setUseQuick] = useState(true);
  const [mode, setMode] = useState<'quick' | 'password'>('password');
  // Google / Microsoft: quali sono attivi sul server e quale è in corso nel browser
  const [sso, setSso] = useState<SsoProvidersDto>({ google: false, microsoft: false });
  const [ssoWait, setSsoWait] = useState<SsoProvider | null>(null);
  const ssoCancel = useRef(false);

  useEffect(() => {
    new Api(serverUrl, null)
      .get<SsoProvidersDto>('/api/auth/sso')
      .then(setSso)
      .catch(() => undefined);
    return () => {
      ssoCancel.current = true;
    };
  }, [serverUrl]);

  const ssoLogin = async (provider: SsoProvider) => {
    ssoCancel.current = false;
    setSsoWait(provider);
    setError(null);
    try {
      const r = await runSso(new Api(serverUrl, null), '/api/auth/sso/start', { provider, device: 'CRM TecnoSide (Windows)' }, () => ssoCancel.current);
      if (r.status === 'ok') await onSsoLogin(serverUrl, r);
      else if (r.status === 'error' && r.message) setError(r.message);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSsoWait(null);
    }
  };

  useEffect(() => {
    let alive = true;
    Promise.all([window.tt.quickGet().catch(() => null), window.tt.helloAvailability().catch(() => null)]).then(([q, h]) => {
      if (!alive) return;
      const ok = h?.status === 'available';
      setHello(ok);
      setSaved(q);
      if (q) setEmail(q.email);
      if (q && ok) setMode('quick');
    });
    return () => {
      alive = false;
    };
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onLogin(serverUrl.trim(), email.trim(), password, hello && useQuick);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const quick = async () => {
    setBusy(true);
    setError(null);
    try {
      await onQuickLogin();
    } catch (err) {
      const status = (err as { status?: string }).status;
      if (status === 'canceled') return;
      setError((err as Error).message);
      // Accesso rapido non più valido: si torna a email e password
      if (status === 'expired' || status === 'missing') {
        setSaved(null);
        setMode('password');
      }
    } finally {
      setBusy(false);
    }
  };

  if (mode === 'quick' && saved) {
    return (
      <div className="login">
        <div className="login-card lock-card">
          <div className="lock-ring">
            <Icon name="fingerprint" size={46} strokeWidth={1.5} />
          </div>
          <h1 style={{ margin: 0 }}>{saved.name ? `Ciao, ${saved.name.split(' ')[0]}` : 'CRM TecnoSide'}</h1>
          <div className="muted small">{busy ? 'Appoggia il dito o guarda la fotocamera…' : saved.email}</div>

          {error && (
            <div className="error" style={{ marginTop: 4 }}>
              {error}
            </div>
          )}

          <button className="btn dark block" type="button" onClick={quick} disabled={busy} style={{ marginTop: 16, padding: '12px 16px', fontSize: 15 }}>
            <Icon name={busy ? 'clock' : 'fingerprint'} size={18} /> {busy ? 'In attesa di Windows Hello…' : 'Entra con impronta o viso'}
          </button>
          <button
            className="btn ghost block"
            type="button"
            disabled={busy}
            style={{ marginTop: 8 }}
            onClick={() => {
              setError(null);
              setMode('password');
            }}
          >
            <Icon name="key" size={15} /> Usa email e password
          </button>
          <div className="muted tiny" style={{ textAlign: 'center', marginTop: 14 }}>
            v{version}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login">
      <form className="login-card" onSubmit={submit}>
        <div className="brand">
          <span className="tile logo-tile">
            <Logo size={46} />
          </span>
          <div>
            <h1>CRM TecnoSide</h1>
            <div className="muted small">{sso.google ? 'Entra con Google o con email e password' : 'Entra con email e password'}</div>
          </div>
        </div>
        <div className="stack" style={{ gap: 0 }}>
          <ErrorBanner message={error} onClose={() => setError(null)} />
          {error && <div style={{ height: 12 }} />}
          <Field label="Email">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
          </Field>
          <Field label="Password">
            <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} required />
          </Field>
          {hello && (
            <label className="login-quick">
              <input type="checkbox" checked={useQuick} onChange={(e) => setUseQuick(e.target.checked)} />
              <Icon name="fingerprint" size={16} /> La prossima volta entra con impronta o viso
            </label>
          )}
          <button className="btn dark block" type="submit" disabled={busy} style={{ marginTop: 6, padding: '12px 16px', fontSize: 15 }}>
            {busy ? 'Accesso in corso…' : 'Entra'}
          </button>
          {(sso.google || sso.microsoft) && (
            <>
              <div className="login-or">
                <span>oppure</span>
              </div>
              {ssoWait ? (
                <div className="login-sso-wait">
                  <SsoLogo provider={ssoWait} />
                  <div className="grow">
                    <b>Completa l'accesso nel browser</b>
                    <div className="muted small">Scegli il tuo account {SSO_LABEL[ssoWait]}: l'app entra da sola.</div>
                  </div>
                  <button
                    type="button"
                    className="btn small"
                    onClick={() => {
                      ssoCancel.current = true;
                      setSsoWait(null);
                    }}
                  >
                    Annulla
                  </button>
                </div>
              ) : (
                <div className="login-sso">
                  {(['google', 'microsoft'] as const)
                    .filter((p) => sso[p])
                    .map((p) => (
                      <button key={p} type="button" className="btn block sso-btn" disabled={busy} onClick={() => ssoLogin(p)}>
                        <SsoLogo provider={p} /> Accedi con {SSO_LABEL[p]}
                      </button>
                    ))}
                </div>
              )}
            </>
          )}
          {saved && hello && (
            <button
              className="btn ghost block"
              type="button"
              disabled={busy}
              style={{ marginTop: 8 }}
              onClick={() => {
                setError(null);
                setMode('quick');
              }}
            >
              <Icon name="fingerprint" size={15} /> Entra con impronta o viso
            </button>
          )}
          <div className="muted tiny" style={{ textAlign: 'center', marginTop: 14 }}>
            v{version}
          </div>
        </div>
      </form>
    </div>
  );
}
